import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router-dom';
import { epcService } from '@/services/epc.service';
import { projectService } from '@/services/project.service';
import { isCepTabKey } from '@/pages/epc/execution/cepTabs';
import { ItemSelect } from '@/components/inventory/ItemSelect';

const { Title, Paragraph, Text } = Typography;

const EpcHubPage: React.FC = () => {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projectId, setProjectId] = useState<string | undefined>();
  const [structureId, setStructureId] = useState<string | undefined>();
  const [wbsEditOpen, setWbsEditOpen] = useState(false);
  const [wbsAddOpen, setWbsAddOpen] = useState(false);
  const [wbsEditNode, setWbsEditNode] = useState<any>(null);
  const [wbsForm] = Form.useForm();
  const [wbsAddForm] = Form.useForm();
  const [drawingOpen, setDrawingOpen] = useState(false);
  const [mrOpen, setMrOpen] = useState(false);
  const [form] = Form.useForm();
  const [mrForm] = Form.useForm();
  const activeTab = searchParams.get('tab') || 'engineering';
  const legacyCepRedirect = isCepTabKey(activeTab);

  const projectsQ = useQuery({
    queryKey: ['epc-projects'],
    queryFn: () => projectService.getProjectsForSelection(),
    staleTime: 60_000,
  });

  const runtimeQ = useQuery({
    queryKey: ['epc-runtime'],
    queryFn: () => epcService.getRuntime(),
    staleTime: 60_000,
  });
  const mrPlatformMode = String((runtimeQ.data as any)?.mrPlatformMode || 'legacy');
  const mrRequiresCatalogItem = mrPlatformMode === 'platform' || mrPlatformMode === 'dual_write';

  const projects = useMemo(() => {
    const raw = projectsQ.data;
    return Array.isArray(raw) ? raw : [];
  }, [projectsQ.data]);

  const progressQ = useQuery({
    queryKey: ['epc-progress', projectId],
    queryFn: () => epcService.getProgress(projectId),
    staleTime: 15_000,
  });
  const drawingsQ = useQuery({
    queryKey: ['epc-drawings', projectId],
    queryFn: () => epcService.getDrawings(projectId),
    staleTime: 15_000,
  });
  const trQ = useQuery({
    queryKey: ['epc-tr', projectId],
    queryFn: () => epcService.getTransmittals(projectId),
    staleTime: 15_000,
  });
  const tqQ = useQuery({
    queryKey: ['epc-tq', projectId],
    queryFn: () => epcService.getTechnicalQueries(projectId),
    staleTime: 15_000,
  });
  const dcnQ = useQuery({
    queryKey: ['epc-dcn', projectId],
    queryFn: () => epcService.getDesignChanges(projectId),
    staleTime: 15_000,
  });
  const mrQ = useQuery({
    queryKey: ['epc-mr', projectId],
    queryFn: () => epcService.getMaterialRequisitions(projectId),
    staleTime: 15_000,
  });
  const structsQ = useQuery({
    queryKey: ['epc-wbs-s', projectId],
    queryFn: () => epcService.getWbsStructures(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const structures = (structsQ.data as any[]) || [];
  const effectiveStructureId =
    structureId || (structures[0] ? String(structures[0].id) : undefined);

  const nodesQ = useQuery({
    queryKey: ['epc-wbs-n', effectiveStructureId],
    queryFn: () => epcService.getWbsNodes(effectiveStructureId!),
    enabled: !!effectiveStructureId,
    staleTime: 15_000,
  });
  const msQ = useQuery({
    queryKey: ['epc-ms', projectId],
    queryFn: () => epcService.getMilestones(projectId),
    staleTime: 30_000,
  });

  const createDrawingMut = useMutation({
    mutationFn: (values: Record<string, unknown>) => epcService.createDrawing(values),
    onSuccess: () => {
      message.success('Drawing registered');
      setDrawingOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['epc-drawings'] });
      void qc.invalidateQueries({ queryKey: ['epc-progress'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const createMrMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcService.createMaterialRequisition({
        ...values,
        projectId,
      }),
    onSuccess: () => {
      message.success('Material requisition created');
      setMrOpen(false);
      mrForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['epc-mr'] });
      void qc.invalidateQueries({ queryKey: ['epc-progress'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const bootstrapMut = useMutation({
    mutationFn: () => epcService.bootstrapWbs(projectId!),
    onSuccess: (data: any) => {
      message.success(data?.created ? 'WBS bootstrap created' : 'WBS already present');
      void qc.invalidateQueries({ queryKey: ['epc-wbs-s'] });
      void qc.invalidateQueries({ queryKey: ['epc-wbs-n'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const layersQ = useQuery({
    queryKey: ['epc-wbs-layers'],
    queryFn: () => epcService.getWbsLayers(),
    staleTime: 60_000,
  });
  const wbsLayers = (layersQ.data as any[]) || [];

  const createWbsMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcService.createWbsNode({
        projectId,
        structureId: effectiveStructureId,
        ...values,
      }),
    onSuccess: () => {
      message.success('WBS node added');
      setWbsAddOpen(false);
      wbsAddForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['epc-wbs-n'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patchWbsMut = useMutation({
    mutationFn: (p: { id: string; values: Record<string, unknown> }) =>
      epcService.patchWbsNode(p.id, p.values),
    onSuccess: () => {
      message.success('WBS node updated');
      setWbsEditOpen(false);
      setWbsEditNode(null);
      void qc.invalidateQueries({ queryKey: ['epc-wbs-n'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const deleteWbsMut = useMutation({
    mutationFn: (id: string) => epcService.deleteWbsNode(id),
    onSuccess: () => {
      message.success('WBS node removed');
      setWbsEditOpen(false);
      setWbsEditNode(null);
      void qc.invalidateQueries({ queryKey: ['epc-wbs-n'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  // Legacy CEP deep links lived on /epc?tab=… — send them to Construction Execution.
  if (legacyCepRedirect) {
    const next = new URLSearchParams(searchParams);
    return <Navigate to={`/epc/execution?${next.toString()}`} replace />;
  }

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          EPC Hub
        </Title>
        <Tag color="blue">Program 12.1 R1</Tag>
        <Select
          style={{ minWidth: 280 }}
          allowClear
          showSearch
          loading={projectsQ.isLoading}
          placeholder={
            projectsQ.isError
              ? 'Failed to load projects'
              : projects.length === 0 && !projectsQ.isLoading
                ? 'No projects for this company'
                : 'Filter by project'
          }
          optionFilterProp="label"
          value={projectId}
          onChange={(v) => {
            setProjectId(v);
            setStructureId(undefined);
          }}
          options={projects.map((p: any) => ({
            value: String(p.id),
            label: `${p.project_number || p.code || ''} ${p.project_name || p.name || p.id}`.trim(),
          }))}
        />
      </Space>
      <Paragraph type="secondary">
        Engineering, document control, material requisitions, WBS, and planning. Construction
        Execution (packages, site progress, permits) lives under its own menu.
      </Paragraph>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            Progress %{' '}
            <Text strong>{String((progressQ.data as any)?.progressPct ?? '—')}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            Approved drawings{' '}
            <Text strong>{String((progressQ.data as any)?.approvedDrawings ?? '—')}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            Pending reviews{' '}
            <Text strong>{String((progressQ.data as any)?.pendingReviews ?? '—')}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            Overdue{' '}
            <Text strong>{String((progressQ.data as any)?.overdueReviews ?? '—')}</Text>
            {' · '}MR{' '}
            <Text strong>{String((progressQ.data as any)?.materialRequisitions ?? '—')}</Text>
          </Card>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          const next = new URLSearchParams(searchParams);
          next.set('tab', key);
          setSearchParams(next, { replace: true });
        }}
        items={[
          {
            key: 'engineering',
            label: 'Engineering',
            children: (
              <Card
                title="Drawing register"
                extra={
                  <Button
                    type="primary"
                    disabled={!projectId}
                    onClick={() => setDrawingOpen(true)}
                  >
                    New drawing
                  </Button>
                }
              >
                <Table
                  size="small"
                  rowKey={(r) => String(r.id)}
                  loading={drawingsQ.isLoading}
                  dataSource={(drawingsQ.data as any[]) || []}
                  columns={[
                    { title: 'No', dataIndex: 'drawing_no', width: 140 },
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Discipline', dataIndex: 'discipline_code', width: 100 },
                    {
                      title: 'Status',
                      dataIndex: 'current_status',
                      width: 100,
                      render: (s) => <Tag>{s}</Tag>,
                    },
                    { title: 'Rev', dataIndex: 'current_rev', width: 70 },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'docctl',
            label: 'Document Control',
            children: (
              <>
                <Card title="Transmittals" style={{ marginBottom: 16 }}>
                  <Table
                    size="small"
                    rowKey={(r) => String(r.id)}
                    dataSource={(trQ.data as any[]) || []}
                    columns={[
                      { title: 'No', dataIndex: 'transmittal_no', width: 140 },
                      { title: 'Subject', dataIndex: 'subject' },
                      { title: 'Status', dataIndex: 'status', width: 100 },
                    ]}
                  />
                </Card>
                <Card title="Technical Queries" style={{ marginBottom: 16 }}>
                  <Table
                    size="small"
                    rowKey={(r) => String(r.id)}
                    dataSource={(tqQ.data as any[]) || []}
                    columns={[
                      { title: 'TQ', dataIndex: 'tq_no', width: 120 },
                      { title: 'Subject', dataIndex: 'subject' },
                      { title: 'Status', dataIndex: 'status', width: 100 },
                    ]}
                  />
                </Card>
                <Card title="Design Changes">
                  <Table
                    size="small"
                    rowKey={(r) => String(r.id)}
                    dataSource={(dcnQ.data as any[]) || []}
                    columns={[
                      { title: 'DCN', dataIndex: 'dcn_no', width: 120 },
                      { title: 'Title', dataIndex: 'title' },
                      { title: 'Status', dataIndex: 'status', width: 100 },
                    ]}
                  />
                </Card>
              </>
            ),
          },
          {
            key: 'mrs',
            label: 'Material Requisitions',
            children: (
              <Card
                title={
                  <Space>
                    <span>MRs</span>
                    <Tag>{mrPlatformMode}</Tag>
                  </Space>
                }
                extra={
                  <Button
                    type="primary"
                    disabled={!projectId}
                    onClick={() => {
                      mrForm.setFieldsValue({
                        lines: [{ description: '', qty: 1, uom: 'PCS' }],
                      });
                      setMrOpen(true);
                    }}
                  >
                    New MR
                  </Button>
                }
              >
                <Table
                  size="small"
                  rowKey={(r) => String(r.id)}
                  dataSource={(mrQ.data as any[]) || []}
                  columns={[
                    { title: 'MR', dataIndex: 'mr_no', width: 120 },
                    { title: 'Title', dataIndex: 'title' },
                    {
                      title: 'Source',
                      dataIndex: 'source',
                      width: 100,
                      render: (v) => v || 'epc',
                    },
                    { title: 'Status', dataIndex: 'status', width: 100 },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'wbs',
            label: 'WBS',
            children: (
              <Card
                title="Structures & nodes"
                extra={
                  <Space>
                    <Select
                      style={{ minWidth: 200 }}
                      placeholder="Structure"
                      value={effectiveStructureId}
                      onChange={setStructureId}
                      options={structures.map((s: any) => ({
                        value: String(s.id),
                        label: `${s.kind}: ${s.name}`,
                      }))}
                    />
                    <Button
                      disabled={!projectId}
                      loading={bootstrapMut.isPending}
                      onClick={() => bootstrapMut.mutate()}
                    >
                      Bootstrap default WBS
                    </Button>
                    <Button
                      disabled={!projectId || !effectiveStructureId}
                      type="primary"
                      onClick={() => setWbsAddOpen(true)}
                    >
                      Add node
                    </Button>
                  </Space>
                }
              >
                {!projectId ? (
                  <Paragraph type="secondary">Select a project to manage WBS.</Paragraph>
                ) : (
                  <Table
                    size="small"
                    rowKey={(r) => String(r.id)}
                    loading={nodesQ.isLoading}
                    dataSource={(nodesQ.data as any[]) || []}
                    onRow={(row) => ({
                      onClick: () => {
                        setWbsEditNode(row);
                        wbsForm.setFieldsValue({
                          code: row.code,
                          title: row.title,
                          wbsLayerCode: row.layer_code,
                          description: row.description,
                          shortName: row.short_name,
                          referenceScopeCode: row.reference_scope_code,
                          costCode: row.cost_code,
                        });
                        setWbsEditOpen(true);
                      },
                      style: { cursor: 'pointer' },
                    })}
                    columns={[
                      { title: 'Code', dataIndex: 'code', width: 120 },
                      { title: 'Title', dataIndex: 'title' },
                      {
                        title: 'Layer',
                        dataIndex: 'layer_code',
                        width: 100,
                        render: (v) => v || '—',
                      },
                      {
                        title: 'Depth',
                        dataIndex: 'tree_depth',
                        width: 70,
                        render: (v) => v ?? '—',
                      },
                      { title: 'Cost code', dataIndex: 'cost_code', width: 120 },
                    ]}
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'planning',
            label: 'Planning',
            children: (
              <Card title="Native milestones (R1 stub — P6/MSP later)">
                <Table
                  size="small"
                  rowKey={(r) => String(r.id)}
                  dataSource={(msQ.data as any[]) || []}
                  columns={[
                    { title: 'Code', dataIndex: 'code', width: 120 },
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Planned', dataIndex: 'planned_date', width: 120 },
                    { title: '%', dataIndex: 'progress_pct', width: 80 },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title="New drawing"
        open={drawingOpen}
        onCancel={() => setDrawingOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createDrawingMut.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) =>
            createDrawingMut.mutate({
              ...values,
              projectId,
            })
          }
        >
          <Form.Item name="drawingNo" label="Drawing no" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="IFC">
            <Select
              options={['IFC', 'IFR', 'IFA', 'AFC', 'AS_BUILT'].map((s) => ({
                value: s,
                label: s,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="New material requisition"
        open={mrOpen}
        onCancel={() => setMrOpen(false)}
        onOk={() => mrForm.submit()}
        confirmLoading={createMrMut.isPending}
        width={720}
      >
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          Mode <Text code>{mrPlatformMode}</Text>
          {mrRequiresCatalogItem
            ? ' — catalog item required on each line for platform Material Request.'
            : ' — free-text lines allowed (legacy epc table).'}
        </Paragraph>
        <Form
          form={mrForm}
          layout="vertical"
          onFinish={(values) => {
            const lines = (values.lines || []).map((line: any) => ({
              description: line.description || line.itemLabel || 'Line',
              qty: line.qty,
              uom: line.uom || 'PCS',
              itemId: line.itemId,
            }));
            createMrMut.mutate({
              mrNo: values.mrNo,
              title: values.title,
              requiredBy: values.requiredBy,
              lines,
            });
          }}
        >
          <Form.Item name="mrNo" label="MR no" rules={[{ required: true }]}>
            <Input placeholder="EPC-MR-001" />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="requiredBy" label="Required by">
            <Input type="date" />
          </Form.Item>
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space
                    key={field.key}
                    align="start"
                    style={{ display: 'flex', marginBottom: 8 }}
                    wrap
                  >
                    <Form.Item
                      {...field}
                      name={[field.name, 'itemId']}
                      label="Item"
                      rules={
                        mrRequiresCatalogItem
                          ? [{ required: true, message: 'Catalog item required' }]
                          : undefined
                      }
                      style={{ minWidth: 240 }}
                    >
                      <ItemSelect
                        style={{ minWidth: 240 }}
                        allowQuickCreate
                        onChange={(id, opt: any) => {
                          const label =
                            opt?.label ||
                            opt?.children ||
                            (typeof opt === 'object' ? opt?.title : undefined);
                          if (label) {
                            mrForm.setFieldValue(['lines', field.name, 'description'], String(label));
                          }
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'description']}
                      label="Description"
                      rules={[{ required: true }]}
                      style={{ minWidth: 180 }}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'qty']}
                      label="Qty"
                      initialValue={1}
                      style={{ width: 100 }}
                    >
                      <InputNumber min={0.0001} style={{ width: 100 }} />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'uom']}
                      label="UOM"
                      initialValue="PCS"
                      style={{ width: 90 }}
                    >
                      <Input />
                    </Form.Item>
                    <Button
                      danger
                      type="link"
                      disabled={fields.length <= 1}
                      onClick={() => remove(field.name)}
                      style={{ marginTop: 30 }}
                    >
                      Remove
                    </Button>
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add({ qty: 1, uom: 'PCS' })} block>
                  Add line
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      <Modal
        title="Add WBS node"
        open={wbsAddOpen}
        onCancel={() => setWbsAddOpen(false)}
        onOk={() => wbsAddForm.submit()}
        confirmLoading={createWbsMut.isPending}
      >
        <Form form={wbsAddForm} layout="vertical" onFinish={(v) => createWbsMut.mutate(v)}>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="wbsLayerCode" label="EPC layer">
            <Select
              allowClear
              options={wbsLayers.map((l: any) => ({
                value: l.code,
                label: `${l.code} · ${l.name}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="shortName" label="Short name">
            <Input />
          </Form.Item>
          <Form.Item name="referenceScopeCode" label="Reference scope">
            <Input />
          </Form.Item>
          <Form.Item name="costCode" label="Cost code">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit WBS node"
        open={wbsEditOpen}
        onCancel={() => {
          setWbsEditOpen(false);
          setWbsEditNode(null);
        }}
        onOk={() => wbsForm.submit()}
        confirmLoading={patchWbsMut.isPending}
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            <Button
              danger
              loading={deleteWbsMut.isPending}
              onClick={() => wbsEditNode && deleteWbsMut.mutate(String(wbsEditNode.id))}
            >
              Remove
            </Button>
            <CancelBtn />
            <OkBtn />
          </>
        )}
      >
        <Form
          form={wbsForm}
          layout="vertical"
          onFinish={(v) =>
            wbsEditNode && patchWbsMut.mutate({ id: String(wbsEditNode.id), values: v })
          }
        >
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="wbsLayerCode" label="EPC layer">
            <Select
              allowClear
              options={wbsLayers.map((l: any) => ({
                value: l.code,
                label: `${l.code} · ${l.name}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="shortName" label="Short name">
            <Input />
          </Form.Item>
          <Form.Item name="referenceScopeCode" label="Reference scope">
            <Input />
          </Form.Item>
          <Form.Item name="costCode" label="Cost code">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EpcHubPage;
